import { Icon, HorizontalDivider, Typography, Image } from '@smartgift/frontend-toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { CardContent, LinearProgressBar } from '@hero/atoms';
import { DetailItem } from '@hero/molecules';
import { getAverageTimeFromSeconds } from '@hero/utils';
import { getAnalyticRates } from '@hero/utils/helper';

import { ChartDescription } from '../ChartDescription';

import * as S from './style';

function RecipientEngagement({ analytics, isContactLess }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const rate = getAnalyticRates(analytics?.acceptanceRates, { format: true });
  const averageAcceptanceRate = getAverageTimeFromSeconds(analytics?.avgAcceptanceTimeInSeconds);

  return (
    <S.Wrapper>
      <Typography variant="body1Bold" style={{ marginBottom: '8px' }}>
        {t('recipientEngagement')}
      </Typography>
      <CardContent>
        <S.Card>
          <DetailItem
            bodyStyle={{
              display: 'flex',
              alignItems: 'center'
            }}
            title={`${t('openRate')}:`}
          >
            <S.BarArea>
              <LinearProgressBar
                backgroundColor="var(--color-blue-100)"
                visualParts={[
                  {
                    percentage: rate.opened,
                    color: 'var(--color-blue-500)'
                  }
                ]}
              />
              <S.Rate>{rate.opened}</S.Rate>
            </S.BarArea>
          </DetailItem>
          <S.Row>
            <ChartDescription
              title={t('giftsOpened')}
              total={analytics?.acceptanceRates?.total}
              current={analytics?.acceptanceRates?.opened}
              percent={rate.opened}
              color="var(--color-blue-500)"
            />
            <ChartDescription
              title={t('giftsUnopened')}
              total={analytics?.acceptanceRates?.total}
              current={analytics?.acceptanceRates?.total - analytics?.acceptanceRates?.opened}
              percent={rate.unopened}
              color="var(--color-blue-100)"
            />
          </S.Row>
        </S.Card>

        <S.Card>
          <DetailItem
            bodyStyle={{
              display: 'flex',
              alignItems: 'center'
            }}
            title={`${t('acceptanceRate')}:`}
          >
            <S.BarArea>
              <LinearProgressBar
                backgroundColor="var(--color-green-100)"
                visualParts={[
                  {
                    percentage: rate.accepted,
                    color: 'var(--color-green-500)'
                  }
                ]}
              />
              <S.Rate>{rate.accepted}</S.Rate>
            </S.BarArea>
          </DetailItem>
          <S.Row>
            <ChartDescription
              title={t('giftsAccepted')}
              total={analytics?.acceptanceRates?.total}
              current={analytics?.acceptanceRates?.accepted}
              percent={rate.accepted}
              color="var(--color-success-default)"
            />
            <ChartDescription
              title={t('giftsNotAccepted')}
              total={analytics?.acceptanceRates?.total}
              current={analytics?.acceptanceRates?.total - analytics?.acceptanceRates?.accepted}
              percent={rate.unaccepted}
              color="var(--color-success-weaker)"
            />
          </S.Row>

          <HorizontalDivider $margin="24px 0" />

          <S.SpeedArea>
            <S.SpeedAreaTitle id="primary">{rate?.accepted}</S.SpeedAreaTitle>
            <Typography color="fg" emphasis="weak" variant="body2">
              {t('averageAcceptanceRateDesc')}
            </Typography>
            <Image
              src={theme.images.speedDialPrimary}
              height="40px"
              alt={t('dashboardSpeedDialTitle')}
            />
          </S.SpeedArea>
        </S.Card>

        {!isContactLess && (
          <S.CardFooter>
            <DetailItem title={`${t('avgAcceptanceTime')}:`}>
              <S.AvgWrapper>
                {averageAcceptanceRate ? (
                  <S.AvgTimeText>
                    <Icon name="tooltip" />
                    {averageAcceptanceRate}
                  </S.AvgTimeText>
                ) : (
                  <S.AvgTimeText>{t('notAcceptedYet')}</S.AvgTimeText>
                )}{' '}
              </S.AvgWrapper>
            </DetailItem>
          </S.CardFooter>
        )}
      </CardContent>
    </S.Wrapper>
  );
}

export { RecipientEngagement };
